import { motion } from "framer-motion";
import { Users, Building2, Briefcase, MessageSquare, GraduationCap } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subMonths, startOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import PeriodFilter, { type PeriodMonths } from "@/components/PeriodFilter";
import PeriodComparisonChart, { type ComparisonDataPoint } from "@/components/PeriodComparisonChart";
import KpiGrid from "@/components/admin/KpiGrid";
import FunnelChart from "@/components/admin/FunnelChart";
import MonthlyEvolutionCharts from "@/components/admin/MonthlyEvolutionCharts";
import EnrollmentsByCourseChart from "@/components/admin/EnrollmentsByCourseChart";
import { Loader2 } from "lucide-react";

/** Aggregate rows by month */
function buildMonthlyData(rows: { created_at: string }[], months: Date[]) {
  const counts = new Map<string, number>();
  months.forEach((m) => counts.set(format(m, "yyyy-MM"), 0));

  rows.forEach((r) => {
    const key = r.created_at.slice(0, 7); // "yyyy-MM"
    if (counts.has(key)) counts.set(key, (counts.get(key) ?? 0) + 1);
  });

  return months.map((m) => ({
    month: format(m, "MMM", { locale: ptBR }),
    value: counts.get(format(m, "yyyy-MM")) ?? 0,
  }));
}

export default function AdminRelatorios() {
  const [period, setPeriod] = useState<PeriodMonths>(6);
  const now = new Date();
  const months = Array.from({ length: period }, (_, i) => startOfMonth(subMonths(now, period - 1 - i)));
  const sinceISO = months[0].toISOString();

  // Previous period range
  const prevMonths = Array.from({ length: period }, (_, i) => startOfMonth(subMonths(now, 2 * period - 1 - i)));
  const prevSinceISO = prevMonths[0].toISOString();

  // KPI stats
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin_relatorio_stats"],
    queryFn: async () => {
      const [profiles, orgs, jobs, contacts, applications, enrollments] = await Promise.all([
        supabase.from("profiles").select("id, profile_status", { count: "exact", head: false }).eq("role", "student"),
        supabase.from("partner_orgs").select("id", { count: "exact", head: true }),
        supabase.from("job_posts").select("id, status", { count: "exact", head: false }),
        supabase.from("contact_requests").select("id, status", { count: "exact", head: false }),
        supabase.from("job_applications").select("id", { count: "exact", head: true }),
        supabase.from("course_applications").select("id, status", { count: "exact", head: false }),
      ]);

      const studentData = profiles.data ?? [];
      const jobData = jobs.data ?? [];
      const contactData = contacts.data ?? [];
      const enrollmentData = enrollments.data ?? [];

      return {
        totalStudents: studentData.length,
        readyStudents: studentData.filter(p => p.profile_status === "ready").length,
        totalPartners: orgs.count ?? 0,
        publishedJobs: jobData.filter(j => j.status === "publicada").length,
        totalContacts: contactData.length,
        approvedContacts: contactData.filter(c => c.status === "approved").length,
        totalApplications: applications.count ?? 0,
        totalEnrollments: enrollmentData.length,
        acceptedEnrollments: enrollmentData.filter(e => e.status === "accepted").length,
      };
    },
  });

  // Monthly evolution data
  const { data: chartData, isLoading: chartsLoading } = useQuery({
    queryKey: ["admin_relatorio_charts", sinceISO, period],
    queryFn: async () => {
      const [signups, applications, contacts] = await Promise.all([
        supabase.from("profiles").select("created_at").eq("role", "student").gte("created_at", sinceISO),
        supabase.from("job_applications").select("created_at").gte("created_at", sinceISO),
        supabase.from("contact_requests").select("created_at").gte("created_at", sinceISO),
      ]);

      return {
        signups: buildMonthlyData(signups.data ?? [], months),
        applications: buildMonthlyData(applications.data ?? [], months),
        contacts: buildMonthlyData(contacts.data ?? [], months),
      };
    },
  });

  // Comparison data (current vs previous period)
  const { data: comparisonData, isLoading: compLoading } = useQuery({
    queryKey: ["admin_relatorio_comparison", sinceISO, prevSinceISO, period],
    queryFn: async () => {
      const [curSignups, prevSignups, curApps, prevApps, curContacts, prevContacts] = await Promise.all([
        supabase.from("profiles").select("created_at").eq("role", "student").gte("created_at", sinceISO),
        supabase.from("profiles").select("created_at").eq("role", "student").gte("created_at", prevSinceISO).lt("created_at", sinceISO),
        supabase.from("job_applications").select("created_at").gte("created_at", sinceISO),
        supabase.from("job_applications").select("created_at").gte("created_at", prevSinceISO).lt("created_at", sinceISO),
        supabase.from("contact_requests").select("created_at").gte("created_at", sinceISO),
        supabase.from("contact_requests").select("created_at").gte("created_at", prevSinceISO).lt("created_at", sinceISO),
      ]);

      return [
        { label: "Cadastros", current: curSignups.data?.length ?? 0, previous: prevSignups.data?.length ?? 0 },
        { label: "Candidaturas", current: curApps.data?.length ?? 0, previous: prevApps.data?.length ?? 0 },
        { label: "Contatos", current: curContacts.data?.length ?? 0, previous: prevContacts.data?.length ?? 0 },
      ] as ComparisonDataPoint[];
    },
  });

  // Enrollments by course (filtered by period)
  const { data: enrollmentsByCourse, isLoading: enrollmentsLoading } = useQuery({
    queryKey: ["admin_relatorio_enrollments_by_course", sinceISO, period],
    queryFn: async () => {
      const [apps, courses] = await Promise.all([
        supabase.from("course_applications").select("course_id, status, created_at").gte("created_at", sinceISO),
        supabase.from("courses").select("id, title"),
      ]);

      const courseMap = new Map((courses.data ?? []).map((c) => [c.id, c.title]));
      const grouped = new Map<string, { total: number; accepted: number }>();

      (apps.data ?? []).forEach((a) => {
        const name = courseMap.get(a.course_id) ?? "Desconhecido";
        const cur = grouped.get(name) ?? { total: 0, accepted: 0 };
        cur.total++;
        if (a.status === "accepted") cur.accepted++;
        grouped.set(name, cur);
      });

      return Array.from(grouped.entries())
        .map(([course, counts]) => ({ course, ...counts }))
        .sort((a, b) => b.total - a.total);
    },
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const kpis = [
    { icon: Users, label: "Talentos Ativos", value: stats?.readyStudents ?? 0, sub: `${stats?.totalStudents ?? 0} cadastrados`, color: "bg-primary/10 text-primary" },
    { icon: Building2, label: "Parceiros", value: stats?.totalPartners ?? 0, color: "bg-accent/20 text-accent" },
    { icon: Briefcase, label: "Vagas Publicadas", value: stats?.publishedJobs ?? 0, color: "bg-success/10 text-success" },
    { icon: MessageSquare, label: "Pedidos Contato", value: stats?.totalContacts ?? 0, sub: `${stats?.approvedContacts ?? 0} aprovados`, color: "bg-cyan-100 text-cyan-600" },
    { icon: GraduationCap, label: "Matrículas", value: stats?.totalEnrollments ?? 0, sub: `${stats?.acceptedEnrollments ?? 0} aceitos`, color: "bg-violet-100 text-violet-600" },
  ];

  const funnelSteps = [
    { label: "Cadastros", value: stats?.totalStudents ?? 0 },
    { label: "Perfil Completo", value: stats?.readyStudents ?? 0 },
    { label: "Candidaturas", value: stats?.totalApplications ?? 0 },
    { label: "Contatos Solicitados", value: stats?.totalContacts ?? 0 },
    { label: "Contatos Aprovados", value: stats?.approvedContacts ?? 0 },
  ];

  const charts = [
    { title: "Cadastros de Talentos", data: chartData?.signups ?? [], color: "hsl(var(--primary))", fillId: "fillSignups" },
    { title: "Candidaturas a Vagas", data: chartData?.applications ?? [], color: "hsl(var(--accent))", fillId: "fillApps" },
    { title: "Pedidos de Contato", data: chartData?.contacts ?? [], color: "hsl(168, 60%, 45%)", fillId: "fillContacts" },
  ];

  return (
    <div className="p-8 max-w-6xl">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Relatórios</h1>
          <p className="text-muted-foreground text-sm mt-1">Métricas agregadas do ecossistema</p>
        </div>
        <PeriodFilter value={period} onChange={setPeriod} />
      </motion.div>

      <KpiGrid kpis={kpis} />

      <MonthlyEvolutionCharts charts={charts} isLoading={chartsLoading} />

      <EnrollmentsByCourseChart data={enrollmentsByCourse ?? []} isLoading={enrollmentsLoading} />

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
        className="bg-card rounded-2xl p-5 border border-border mb-8">
        <h3 className="font-heading font-semibold text-foreground mb-4">Comparativo entre Períodos</h3>
        <div className="h-52">
          <PeriodComparisonChart
            data={comparisonData ?? []}
            isLoading={compLoading}
            periodLabel={`Últimos ${period} meses`}
            previousLabel={`${period} meses anteriores`}
          />
        </div>
      </motion.div>

      <FunnelChart steps={funnelSteps} />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="bg-primary/5 rounded-xl p-4 border border-primary/10">
        <p className="text-sm text-muted-foreground text-center">📊 Dados agregados — sem identificação pessoal. Conforme LGPD.</p>
      </motion.div>
    </div>
  );
}
